import { useState, useEffect } from 'react';
import styles from '../AuthModal/AuthModal.module.css';

const EmailInput = ({
  value,
  onChange,
  onBlur,
  onKeyDown,
  placeholder = "email address",
  id,
  name,
  autoComplete = "email",
  className = "",
  hasError = false,
  ...props
}) => {
  const [validationState, setValidationState] = useState('idle'); // idle, valid, invalid
  const [validationMessage, setValidationMessage] = useState('');
  const [shouldShowValidation, setShouldShowValidation] = useState(false);

  // Email validation regex - stricter for incomplete domains
  const emailRegex = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?\.(?:[a-zA-Z]{2,4}(?:\.[a-zA-Z]{2,3})?)$/;

  // Validate email format
  const validateEmail = (emailValue) => {
    if (!emailValue) {
      setValidationState('idle');
      setValidationMessage('');
      return;
    }

    if (emailRegex.test(emailValue)) {
      setValidationState('valid');
      setValidationMessage('Valid email address');
    } else {
      setValidationState('invalid');
      if (emailValue.indexOf('@') === -1) {
        setValidationMessage('Email must contain @');
      } else if (emailValue.indexOf('.') === -1 || emailValue.lastIndexOf('.') < emailValue.indexOf('@')) {
        setValidationMessage('Email must contain a valid domain');
      } else {
        setValidationMessage('Please enter a valid email address');
      }
    }
  };

  // Handle blur - show validation when user leaves field
  const handleBlur = (e) => {
    // Only run email validation if this is actually an email field
    if (autoComplete === 'email' || placeholder.toLowerCase().includes('email')) {
      setShouldShowValidation(true);
      validateEmail(value);
    }
    if (onBlur) {onBlur(e);}
  };

  // Handle key down - show validation on Enter
  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      // Only run email validation if this is actually an email field
      if (autoComplete === 'email' || placeholder.toLowerCase().includes('email')) {
        setShouldShowValidation(true);
        validateEmail(value);
      }
    }
    if (onKeyDown) {onKeyDown(e);}
  };

  // Handle change - hide validation while typing if it was invalid, but keep valid state
  const handleChange = (e) => {
    // Only run email validation logic if this is actually an email field
    if (autoComplete === 'email' || placeholder.toLowerCase().includes('email')) {
      // Only clear validation when user starts typing again after an error, not for valid state
      if (shouldShowValidation && validationState === 'invalid') {
        setShouldShowValidation(false);
        setValidationState('idle');
        setValidationMessage('');
      }
    }
    // Don't clear valid state when typing continues
    if (onChange) {onChange(e);}
  };

  // Clear validation when input is empty
  useEffect(() => {
    if (!value) {
      setShouldShowValidation(false);
      setValidationState('idle');
      setValidationMessage('');
    }
  }, [value]);

  // Validation icons - only show when validation should be displayed
  const ValidationIcon = () => {
    if (!shouldShowValidation) {return null;}

    switch (validationState) {
      case 'valid':
        return (
          <div className={`${styles.emailValidationIcon} ${styles.valid}`}>
            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
        );
      case 'invalid':
        return (
          <div className={`${styles.emailValidationIcon} ${styles.invalid}`}>
            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </div>
        );
      default:
        return null;
    }
  };

  // Only apply validation styles when validation should be shown
  const getInputClass = () => {
    let classes = `${styles.formInput} ${className}`;

    // Apply error styling from parent component (hasError) or internal email validation
    if (hasError) {
      classes += ` ${styles.invalid}`;
    } else if ((autoComplete === 'email' || placeholder.toLowerCase().includes('email')) && 
               shouldShowValidation && validationState === 'invalid') {
      classes += ` ${styles.invalid}`;
    }

    return classes.trim();
  };

  return (
    <div className={styles.emailInputContainer}>
      <input
        type="email"
        placeholder={placeholder}
        id={id}
        name={name}
        className={getInputClass()}
        autoComplete={autoComplete}
        value={value}
        onChange={handleChange}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        style={{ paddingRight: '16px' }}
        aria-describedby={shouldShowValidation && validationMessage ? `${id}-validation` : undefined}
        {...props}
      />
    </div>
  );
};

export default EmailInput;
